import { Groups } from '@mui/icons-material';
import { Box, Stack, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { createAndSendExternalInvitation } from '@/domain/external-invitation/actions/create-and-send-external-invitation.action';
import { selectExternalInvitationsForScopes } from '@/domain/external-invitation/external-invitations.selectors';
import { getScopeFromModeSectorAndCategory } from '@/domain/external-invitation/utils/scopes/get-scope';
import { getScopesFromModeAndSector } from '@/domain/external-invitation/utils/scopes/get-scopes';
import { ScoreType, Sector } from '@/domain/impact/impact.entity';
import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { PageTitle } from '@/ui/components/PageTitle';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { InviteSubmitData } from '@/ui/pages/app/components/stakeholders/assign-external/components/FormExternalInvite';
import { ModalStakeholders } from '@/ui/pages/app/components/stakeholders/ModalStakeholders';

import { VenueStructureMainContent } from './components/VenueStructureMainContent';
import { VenueStructureQuizPanel } from './components/VenueStructureQuizPanel';
import { prepareModalStakeholdersItems } from './utils/prepare-modal-stakehodlers-iems';

export function VenueStructure() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [selectedTopic, setSelectedTopic] = useState<ScoreType>('co2');
  const [selectedSector, setSelectedSector] = useState<Sector>('TEAM');
  const [selectedDetailsValueId, setSelectedDetailsValueId] =
    useState<string>('');

  const [stakeholdersModalOpen, setStakeholdersModalOpen] = useState(false);

  const project = useAppSelector(selectCurrentProjectVenue);

  const onClickBack = () => {
    if (project) navigate('/app/projects/venue/' + project.id);
  };

  const onClickStakeholders = () => {
    setStakeholdersModalOpen(true);
  };

  const existingInvitationsPerSector = {
    SITE: useAppSelector(
      selectExternalInvitationsForScopes(
        getScopesFromModeAndSector('venue-structure', 'SITE'),
      ),
    ),
    TEAM: useAppSelector(
      selectExternalInvitationsForScopes(
        getScopesFromModeAndSector('venue-structure', 'TEAM'),
      ),
    ),
    PUBLIC: useAppSelector(
      selectExternalInvitationsForScopes(
        getScopesFromModeAndSector('venue-structure', 'PUBLIC'),
      ),
    ),
  };

  const modalStakeholdersItems = prepareModalStakeholdersItems(
    existingInvitationsPerSector,
  );

  const onSubmitInvite = async (data: InviteSubmitData) => {
    const scope = getScopeFromModeSectorAndCategory(
      'venue-structure',
      data.sector,
      data.category,
    );
    const params = {
      name: data.name,
      email: data.email,
      limitDate: data.limitDate,
      eventPosition: undefined,
      title: '',
      projectId: project?.id || -1,
      sector: data.sector,
      scope: scope,
    };
    await dispatch(createAndSendExternalInvitation(params));
  };

  return (
    <>
      <ModalStakeholders
        open={stakeholdersModalOpen}
        onClose={() => setStakeholdersModalOpen(false)}
        items={modalStakeholdersItems}
        onSubmitInvite={onSubmitInvite}
      />
      <Stack width={'calc(100% - 21rem)'} sx={{ gap: 6 }}>
        <PageTitle
          title={translate('ProjectsVenue.manage-structure')}
          subtitle={translate('ProjectsVenue.manage-structure-description')}
          backButtonText={project?.name || ''}
          onClickBack={onClickBack}
          rightComponent={
            <StakeholdersVenueStructureButton
              onClickStakeholders={onClickStakeholders}
            />
          }
        />
        <VenueStructureMainContent
          setSelectedTopic={setSelectedTopic}
          setSelectedSector={setSelectedSector}
          setSelectedDetailsValueId={setSelectedDetailsValueId}
        />
        <VenueStructureQuizPanel
          projectId={project?.id || -1}
          selectedTopic={selectedTopic}
          selectedSector={selectedSector}
          selectedDetailsValueId={selectedDetailsValueId}
          onSubmitInvite={onSubmitInvite}
        />
      </Stack>
    </>
  );
}

function StakeholdersVenueStructureButton({
  onClickStakeholders,
}: {
  onClickStakeholders: () => void;
}) {
  return (
    <Tooltip
      title={translate('Projects.stakeholders-button-info')}
      placement="bottom"
    >
      {/* hack: box is used to make tooltip visible */}
      <Box>
        <FSPrimaryButton startIcon={<Groups />} onClick={onClickStakeholders}>
          {translate('InviteExternalToFill.stakeholders')}
        </FSPrimaryButton>
      </Box>
    </Tooltip>
  );
}
